// Номерные страницы публичного каталога — одно правило на все поверхности.
//
// ─────────────────────────────────────────────────────────────────────────────
// Зачем номерные страницы, если выдача курсорная
// ─────────────────────────────────────────────────────────────────────────────
// Каталог листается кнопкой «Показать ещё» поверх курсорной выдачи. Для человека
// этого достаточно, для робота — нет: кнопка не меняет адрес, поэтому дальше
// первой порции идти НЕКУДА. Курсы при этом в карте сайта есть, но ни одной
// внутренней ссылки на них не существует — поисковик знает об их существовании и
// не получает от каталога ни веса, ни контекста.
//
// Поэтому у каждой порции появляется собственный адрес (`?page=2`), и по нему
// ходят обычные `<a href>`. Кнопка остаётся: она по-прежнему ДОБАВЛЯЕТ порцию к
// уже показанным, а адрес лишь отражает, до какой страницы человек долистал.
//
// ─────────────────────────────────────────────────────────────────────────────
// Почему `?page=`, а не сегмент пути
// ─────────────────────────────────────────────────────────────────────────────
// Всё остальное состояние списка (фильтры, поиск, сортировка) уже живёт в
// запросе. Сегмент пути (`/catalog/page/2`) завёл бы ВТОРУЮ модель состояния
// рядом с первой — и всё равно тащил бы `?area=…` хвостом. Он же занял бы слово
// `page` в пространстве слагов курсов (`/catalog/{slug}`) и потребовал бы
// собственной обработки в языковом префиксе (`/de/catalog/page/2`), тогда как
// запрос префикс переживает без единой строчки кода.
//
// Запрет `?lang=` из плана SEO сюда не распространяется: он про ЯЗЫКОВЫЕ версии,
// которые поисковик обязан считать одной страницей. Страницы пагинации —
// РАЗНЫЕ страницы с разным содержимым, каждая канонична сама на себя.
//
// ─────────────────────────────────────────────────────────────────────────────
// Почему номерная страница не спорит с курсором
// ─────────────────────────────────────────────────────────────────────────────
// `ORDER BY` каталога — ПОЛНЫЙ порядок: хвост ключа `(origin, id)` уникален,
// поэтому двух строк с одинаковым ключом не бывает. У полного порядка
// «страница N» определена однозначно, как ни приходи: обход курсором и `OFFSET
// (N-1)*size` идут по одной и той же последовательности. Курсор остаётся
// механизмом ПРОДОЛЖЕНИЯ (он не зависит от глубины и переживает сдвиг ленты),
// номер — механизмом ВХОДА. Дрейф у них общий и ровно тот же, что был до
// номерных страниц (см. `queries/list-catalog.ts`), нового класса он не заводит.

/** Размер страницы каталога — общий для выдачи, ссылок и серверной головы. */
export const CATALOG_PAGE_SIZE = 24;

/**
 * Потолок номерных страниц.
 *
 * Публичный адрес принимает любое число, и без потолка `?page=100000` означал бы
 * `OFFSET 2400000` на каждый заход робота-переборщика. Двадцать страниц — это
 * 480 курсов при нынешних 80; упрёмся — поднимем сознательно, вместе с решением
 * о том, что делать с глубиной обхода.
 */
export const CATALOG_MAX_PAGE = 20;

/**
 * Номер страницы из сырого значения адреса. Всё непонятное — первая страница.
 *
 * Никогда не бросает и не отдаёт ошибку: это публичный адрес, и `?page=abc`
 * обязан показать каталог, а не 400.
 */
export function parseCatalogPage(raw: unknown): number {
	const value = Array.isArray(raw) ? raw[0] : raw;
	if (typeof value === "number") return normalizePage(value);
	if (typeof value !== "string") return 1;
	if (!/^\d{1,7}$/.test(value.trim())) return 1;
	return normalizePage(Number.parseInt(value, 10));
}

function normalizePage(value: number): number {
	if (!Number.isFinite(value) || !Number.isInteger(value) || value < 1) return 1;
	return value;
}

/** Смещение выдачи для номерной страницы. */
export function catalogPageOffset(page: number, pageSize = CATALOG_PAGE_SIZE): number {
	return (normalizePage(page) - 1) * pageSize;
}

/**
 * Сколько всего страниц у ленты из `total` записей.
 *
 * Пустая лента — одна страница, а не ноль: `/catalog` существует и отвечает 200
 * даже когда показывать нечего, и «страница 1 из 0» — это неверное утверждение.
 */
export function catalogPageCount(total: number, pageSize = CATALOG_PAGE_SIZE): number {
	if (!Number.isFinite(total) || total <= 0) return 1;
	return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * Хвост адреса страницы: `""` для первой, `"?page=N"` для остальных.
 *
 * Первая страница НЕ получает `?page=1` нигде и никогда — иначе у неё два
 * адреса, то есть дубль в индексе на ровном месте.
 */
export function catalogPageSearch(page: number): string {
	const value = normalizePage(page);
	return value <= 1 ? "" : `?page=${value}`;
}
